// ============================================================
// MySQLWindowFunctionsAndAnalytics.jsx
//
// Window Functions & Analytics (MySQL)
// Foundation · Module 09
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

/* Icons */
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import FunctionsIcon from "@mui/icons-material/Functions";
import TimelineIcon from "@mui/icons-material/Timeline";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ───────────────── SUB COMPONENTS ───────────────── */

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 18px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                <Typography sx={{ fontWeight: 700, color: BRAND.white }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const copy = () => {
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <Box onClick={copy} sx={{ cursor: "pointer", color: BRAND.gold }}>
                        {copied ? <DoneIcon /> : <ContentCopyIcon />}
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2 }}>
                {lines.map((l, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                        {l.comment && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted }}>
                                -- {l.comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText }}>
                            {l.cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    background: BRAND.surface,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                    <Typography sx={{ color: BRAND.white }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 2, background: "rgba(0,0,0,0.25)" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

/* ───────────────── MAIN PAGE ───────────────── */

export default function MySQLWindowFunctionsAndAnalytics() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 09"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    Window Functions & <span style={{ color: BRAND.gold }}>Analytics</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 780 }}>
                    Window functions allow SQL to perform analytics without collapsing rows.
                    They are essential for rankings, running totals, comparisons, and reports.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* WHY */}
                <SectionTitle icon={<LightbulbIcon />} label="Why Window Functions Matter" />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    Traditional GROUP BY collapses rows. Window functions keep rows intact
                    while adding analytical insights — a must‑have skill for modern SQL.
                </Typography>

                {/* KEY CONCEPTS */}
                <SectionTitle icon={<MenuBookIcon />} label="Core Concepts" />
                <Grid container spacing={2}>
                    {[
                        {
                            icon: <FunctionsIcon />,
                            title: "OVER() Clause",
                            description: "Defines the window of rows used for calculations.",
                        },
                        {
                            icon: <TimelineIcon />,
                            title: "PARTITION BY",
                            description: "Resets calculations for logical groups.",
                        },
                        {
                            icon: <EqualizerIcon />,
                            title: "ORDER BY",
                            description: "Controls the order inside the window.",
                        },
                        {
                            icon: <AnalyticsIcon />,
                            title: "Ranking Functions",
                            description: "ROW_NUMBER, RANK, DENSE_RANK assign positions.",
                        },
                        {
                            icon: <AnalyticsIcon />,
                            title: "Navigation Functions",
                            description: "LAG and LEAD compare rows across time.",
                        },
                    ].map((c) => (
                        <Grid item xs={12} sm={6} md={4} key={c.title}>
                            <ConceptCard {...c} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* PRACTICE */}
                <SectionTitle icon={<CodeIcon />} label="Hands‑On: Window Functions" />

                <CodeBlock
                    lines={[
                        {
                            comment: "Rank students by score",
                            cmd: `SELECT name, score,
ROW_NUMBER() OVER (ORDER BY score DESC) AS row_num
FROM students;`,
                        },
                    ]}
                />

                <ExpandableExample title="Partitioned Ranking" icon={<AnalyticsIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Rank students per course",
                                cmd: `SELECT student_id, course_id, score,
RANK() OVER (PARTITION BY course_id ORDER BY score DESC) AS rank_in_course
FROM enrollments;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="Running Totals" icon={<TimelineIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Running total of orders",
                                cmd: `SELECT created_at, total,
SUM(total) OVER (ORDER BY created_at) AS running_total
FROM orders;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="LAG & LEAD Comparison" icon={<AnalyticsIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Compare order totals",
                                cmd: `SELECT id, total,
LAG(total) OVER (ORDER BY id) AS previous_total,
LEAD(total) OVER (ORDER BY id) AS next_total
FROM orders;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LEARNED */}
                <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                <Grid container spacing={2}>
                    {[
                        "What window functions are",
                        "How OVER(), PARTITION BY, and ORDER BY work",
                        "How to rank rows without GROUP BY",
                        "How to compute running totals",
                        "How to compare rows using LAG and LEAD",
                        "Why analytics queries are cleaner with windows",
                    ].map((i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold }} />
                                <Typography sx={{ color: BRAND.muted }}>{i}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FOOTER */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        💡 <strong>Pro Tip:</strong> If you find yourself using subqueries for
                        rankings or running totals, window functions are usually cleaner and faster.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
